import { useState, useEffect } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import {
    X, Briefcase, MapPin, Clock, Euro, Tag, Calendar,
    User, Building, Mail, Phone, Edit2, Trash2, CheckCircle
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import {
    deleteOffer,
    closeOffer,
    getTypeColor,
    getTypeLabel,
    getUrgencyColor,
    getUrgencyLabel,
    getCompensationColor,
    getCompensationLabel,
    getDurationColor,
    getDurationLabel,
    getCreatorProfile
} from '../../services/workOffersService';

function WorkOfferDetail({ offer, onClose, onUpdated }) {
    const { t } = useTranslation();
    const { user } = useAuth();

    const [creatorProfile, setCreatorProfile] = useState(null);
    const [creatorPhoto, setCreatorPhoto] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [showConfirmClose, setShowConfirmClose] = useState(false);

    const isOwner = user && (user.id === offer.peopleid || user.id === offer.peopleId);
    const offerType = offer.offertype || offer.offerType;
    const urgencyBadge = offer.urgencybadge || offer.urgencyBadge;
    const compensation = offer.compensation;
    const duration = offer.duration;
    const skills = offer.skillsoffered || offer.skillsOffered;
    const location = offer.location;
    const createdAt = offer.createdat || offer.createdAt;
    const status = offer.status;
    const creatorUuid = offer.creatoruuid || offer.creatorUuid;

    useEffect(() => {
        if (creatorUuid) {
            loadCreatorProfile();
        }
    }, [creatorUuid]);

    const loadCreatorProfile = async () => {
        const result = await getCreatorProfile(creatorUuid);
        if (result.success) {
            setCreatorProfile(result.profile);
            setCreatorPhoto(result.photoUrl);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('fr-BE', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            const result = await deleteOffer(offer.id, user.id);
            if (result.success) {
                onUpdated();
                onClose();
            } else {
                alert(result.message || t.errors?.generic || 'Une erreur est survenue');
            }
        } catch (error) {
            alert(t.errors?.generic || 'Une erreur est survenue');
        } finally {
            setIsDeleting(false);
            setShowConfirmDelete(false);
        }
    };

    const handleClose = async () => {
        setIsClosing(true);
        try {
            const result = await closeOffer(offer.id, user.id);
            if (result.success) {
                onUpdated();
                onClose();
            } else {
                alert(result.message || t.errors?.generic || 'Une erreur est survenue');
            }
        } catch (error) {
            alert(t.errors?.generic || 'Une erreur est survenue');
        } finally {
            setIsClosing(false);
            setShowConfirmClose(false);
        }
    };

    const creatorName = `${offer.creatorfirstname || offer.creatorFirstName || ''} ${offer.creatorlastname || offer.creatorLastName || ''}`.trim();
    const creatorCompany = offer.creatorcompany || offer.creatorCompany;

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />

            {/* Modal */}
            <div className="relative bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div
                    className="p-4 text-white"
                    style={{ background: `linear-gradient(to right, ${getTypeColor(offerType)}, ${getTypeColor(offerType)}dd)` }}
                >
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            {/* Badges */}
                            <div className="flex flex-wrap gap-1.5 mb-2">
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/20">
                                    {getTypeLabel(offerType)}
                                </span>
                                <span
                                    className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                                    style={{ backgroundColor: getUrgencyColor(urgencyBadge) }}
                                >
                                    {getUrgencyLabel(urgencyBadge)}
                                </span>
                                {status === 'closed' && (
                                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-600 text-white">
                                        {t.offers?.closed || 'Clôturée'}
                                    </span>
                                )}
                            </div>
                            {/* Title */}
                            <h2 className="text-xl font-bold">{offer.title}</h2>
                        </div>
                        <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg ml-2">
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {/* Creator info */}
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        {creatorPhoto ? (
                            <img
                                src={creatorPhoto}
                                alt={creatorName}
                                className="w-12 h-12 rounded-full object-cover"
                            />
                        ) : (
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold text-lg">
                                {creatorName.charAt(0).toUpperCase()}
                            </div>
                        )}
                        <div className="flex-1">
                            <p className="font-semibold text-gray-900">{creatorName}</p>
                            {creatorCompany && (
                                <p className="text-sm text-gray-600 flex items-center gap-1">
                                    <Building className="h-3 w-3" />
                                    {creatorCompany}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Meta info */}
                    <div className="grid grid-cols-2 gap-3">
                        {/* Date */}
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span>{formatDate(createdAt)}</span>
                        </div>

                        {/* Location */}
                        {location && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <MapPin className="h-4 w-4 text-gray-400" />
                                <span>{location}</span>
                            </div>
                        )}

                        {/* Compensation */}
                        {compensation && (
                            <div className="flex items-center gap-2 text-sm">
                                <Euro className="h-4 w-4 text-gray-400" />
                                <span
                                    className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                                    style={{ backgroundColor: getCompensationColor(compensation) }}
                                >
                                    {getCompensationLabel(compensation)}
                                </span>
                            </div>
                        )}

                        {/* Duration */}
                        {duration && duration !== 'undefined' && (
                            <div className="flex items-center gap-2 text-sm">
                                <Clock className="h-4 w-4 text-gray-400" />
                                <span
                                    className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                                    style={{ backgroundColor: getDurationColor(duration) }}
                                >
                                    {getDurationLabel(duration)}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    <div>
                        <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <Briefcase className="h-4 w-4" />
                            {t.offers?.description || 'Description'}
                        </h3>
                        <p className="text-gray-700 whitespace-pre-wrap">{offer.description}</p>
                    </div>

                    {/* Skills */}
                    {skills && (
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                <Tag className="h-4 w-4" />
                                {t.offers?.skillsOffered || 'Compétences proposées'}
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {skills.split(',').map((skill, idx) => (
                                    <span
                                        key={idx}
                                        className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                                    >
                                        {skill.trim()}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Contact info (if viewing someone else's offer) */}
                    {!isOwner && creatorProfile && (
                        <div className="border-t pt-4">
                            <h3 className="font-semibold text-gray-900 mb-3">
                                {t.offers?.contact || 'Contacter'}
                            </h3>
                            <div className="space-y-2">
                                {creatorProfile.email && (
                                    <a
                                        href={`mailto:${creatorProfile.email}`}
                                        className="flex items-center gap-2 text-green-600 hover:text-green-700"
                                    >
                                        <Mail className="h-4 w-4" />
                                        {creatorProfile.email}
                                    </a>
                                )}
                                {creatorProfile.phone && (
                                    <a
                                        href={`tel:${creatorProfile.phone}`}
                                        className="flex items-center gap-2 text-green-600 hover:text-green-700"
                                    >
                                        <Phone className="h-4 w-4" />
                                        {creatorProfile.phone}
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer - Owner actions */}
                {isOwner && status !== 'closed' && (
                    <div className="p-4 border-t bg-gray-50 flex gap-3">
                        <button
                            onClick={() => setShowConfirmClose(true)}
                            className="flex-1 py-2.5 px-4 border border-green-500 text-green-600 rounded-lg font-medium hover:bg-green-50 flex items-center justify-center gap-2"
                        >
                            <CheckCircle className="h-4 w-4" />
                            {t.offers?.markClosed || 'Clôturer'}
                        </button>
                        <button
                            onClick={() => setShowConfirmDelete(true)}
                            className="py-2.5 px-4 border border-red-300 text-red-600 rounded-lg font-medium hover:bg-red-50"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                )}

                {/* Confirm delete dialog */}
                {showConfirmDelete && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-xl p-4 max-w-sm w-full">
                            <h3 className="font-semibold text-gray-900 mb-2">
                                {t.offers?.confirmDelete || 'Supprimer cette offre ?'}
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                {t.offers?.deleteWarning || 'Cette action est irréversible.'}
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowConfirmDelete(false)}
                                    className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100"
                                >
                                    {t.common?.cancel || 'Annuler'}
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={isDeleting}
                                    className="flex-1 py-2 px-4 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 disabled:opacity-50"
                                >
                                    {isDeleting ? '...' : (t.common?.delete || 'Supprimer')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Confirm close dialog */}
                {showConfirmClose && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-xl p-4 max-w-sm w-full">
                            <h3 className="font-semibold text-gray-900 mb-2">
                                {t.offers?.confirmClose || 'Clôturer cette offre ?'}
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                {t.offers?.closeWarning || "L'offre ne sera plus visible dans les recherches."}
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowConfirmClose(false)}
                                    className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100"
                                >
                                    {t.common?.cancel || 'Annuler'}
                                </button>
                                <button
                                    onClick={handleClose}
                                    disabled={isClosing}
                                    className="flex-1 py-2 px-4 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 disabled:opacity-50"
                                >
                                    {isClosing ? '...' : (t.offers?.close || 'Clôturer')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default WorkOfferDetail;
